// :cow: Cosmose CONFIDENTIAL :iso:
import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { ConfigService } from '@med/app/core/config/config.service';
import { LanguageService } from '@med/app/core/language/service/language.service';
import { concatMap } from 'rxjs/operators';
import { PlannerFullDemoDataProviderService } from '@med/app/home/planner-full-demo/planner-full-demo-data-provider.service';
import { PlannerFullDemoService } from '@med/app/home/planner-full-demo/planner-full-demo.service';

@Injectable({providedIn: 'root'})
export class PlannerFullDemoInterceptor implements HttpInterceptor {

  constructor(
    private dataProvider: PlannerFullDemoDataProviderService,
    private configService: ConfigService,
    private languageService: LanguageService,
    private plannerFullDemoService: PlannerFullDemoService,
  ) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.plannerFullDemoService.isDemoEnabled()) {
      return next.handle(req);
    }

    if (!req.url.includes(this.configService.getConfig().apiUrl)) {
      return next.handle(req);
    }

    return this.getBody(req.url, req.method).pipe(
      concatMap(body => {
        if (body) {
          const response = new HttpResponse({status: 200, body: body});

          return of(response);
        }

        return next.handle(req);
      }),
    );
  }

  private getBody(url: string, method: string): Observable<any> {
    const stepId = this.plannerFullDemoService.getStepId();

    return this.languageService.getLanguage().pipe(
      concatMap(language => {
        switch (true) {
          case (method === 'GET' && url.endsWith('/platforms/all-available')):
            return this.dataProvider.getPlatforms(language);

          case (method === 'GET' && url.includes('/ad-formats')):
            return this.dataProvider.getAdformats(language);

          case url.endsWith('/campaigns/demo'):
          case url.endsWith('/campaigns/demo/audience-segments/business-categories'):
          case url.endsWith('/campaigns/demo/audience-segments/cities'):
          case url.endsWith('/campaigns/demo/audience-segments/competitors'):
          case url.endsWith('/campaigns/demo/audience-segments/relevant-location-categories'):
          case method === 'PUT' && url.endsWith('/campaigns/demo/platforms'):
          case method === 'PUT' && url.endsWith('/campaigns/demo/ad-formats'):
            return this.dataProvider.getCampaign(stepId, language);

          case url.includes('/competitor-brands'):
            return this.dataProvider.getCompetitorBrands(language);
          case url.includes('/location-unit-groups'):
            return this.dataProvider.getLocationUnitGroups(language);
          case url.includes('/estimates'):
            return this.dataProvider.getEstimates(stepId);
          case url.includes('/demographics'):
            return this.dataProvider.getDemographics(language);
          case url.includes('/campaign-presets'):
            return this.dataProvider.getCampaignPresets(language);
          case url.includes('/campaign-defaults'):
            return this.dataProvider.getCampaignDefaults();
          case url.includes('/countries'):
            return this.dataProvider.getCountries(language);
          case url.includes('/platforms/recommended'):
            return this.dataProvider.getRecommendedPlatforms(language);
          case url.includes('/cities'):
            return this.dataProvider.getCities(language);
          case url.includes('/spot-plan'):
            return this.dataProvider.getSpotplan(language);
          case url.includes('/budget'):
            return this.dataProvider.getBudget();
          case url.includes('/publish'):
            return this.dataProvider.getCampaign(stepId, language);
          case url.includes('/clients'):
            return this.dataProvider.getClients();
          case url.endsWith('/campaigns'):
            return this.dataProvider.getCampaignList(stepId, language);
          default:
            throw new Error(`No provider defined for url: ${ url }`);
        }
      }),
    );

  }
}
